import { RISK_TIER, RISK_TIER_CUMULATIVE_TARGETS } from '../data/riskOutlook.js';

function probTargetNotDrawn(targetWeight, otherTierWeights, standardCount, draws) {
  if (draws === 0) return 1;
  const totalOthers =
    otherTierWeights.reduce((s, w) => s + w, 0) + standardCount;
  if (totalOthers < draws) return 0;
  const totalWeight = targetWeight + totalOthers;
  if (totalWeight <= 0) return 0;
  let result = 0;
  for (let i = 0; i < otherTierWeights.length; i++) {
    const w = otherTierWeights[i];
    if (w <= 0) continue;
    const remaining = [
      ...otherTierWeights.slice(0, i),
      ...otherTierWeights.slice(i + 1)
    ];
    result +=
      (w / totalWeight) *
      probTargetNotDrawn(targetWeight, remaining, standardCount, draws - 1);
  }
  if (standardCount > 0) {
    result +=
      (standardCount / totalWeight) *
      probTargetNotDrawn(
        targetWeight,
        otherTierWeights,
        standardCount - 1,
        draws - 1
      );
  }
  return result;
}

export function inclusionProbability({
  targetWeight,
  otherTierWeights,
  standardCount,
  draws
}) {
  return (
    1 -
    probTargetNotDrawn(targetWeight, otherTierWeights, standardCount, draws)
  );
}

function inclusionForTier(weights, tierIdx, standardCount, draws) {
  const targetWeight = weights[tierIdx];
  const otherTierWeights = weights.filter((_, i) => i !== tierIdx);
  return inclusionProbability({
    targetWeight,
    otherTierWeights,
    standardCount,
    draws
  });
}

function bisectForTier({
  weights,
  tierIdx,
  standardCount,
  draws,
  target,
  lo,
  hi,
  tol,
  maxIter
}) {
  const trial = (w) => {
    const next = weights.slice();
    next[tierIdx] = w;
    return inclusionForTier(next, tierIdx, standardCount, draws);
  };
  let low = lo;
  let high = hi;
  let pLow = trial(low);
  let pHigh = trial(high);
  let safetyExpand = 0;
  while (pHigh < target && safetyExpand < 60) {
    high *= 2;
    pHigh = trial(high);
    safetyExpand++;
  }
  while (pLow > target && low > 1e-12 && safetyExpand < 120) {
    low /= 2;
    pLow = trial(low);
    safetyExpand++;
  }
  for (let i = 0; i < maxIter; i++) {
    const mid = 0.5 * (low + high);
    const pMid = trial(mid);
    if (Math.abs(pMid - target) < tol) return mid;
    if (pMid < target) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return 0.5 * (low + high);
}

// solveTierWeights is a pure function of (poolSize, draws) for the
// default targets, but it is a nested bisection wrapped around a
// recursive draw-probability calculation - the single most expensive
// thing in a resolved game. A whole game only ever asks for a handful
// of distinct (poolSize, draws) pairs, so the same solve was being
// repeated for every year of every simulated game.
//
// Cached results are copied out, so a caller mutating what it gets
// back cannot poison the cache. Non-default targets or solver options
// bypass the cache entirely.
const defaultTierWeightCache = new Map();

export function solveTierWeights({
  poolSize,
  draws,
  targets = RISK_TIER_CUMULATIVE_TARGETS,
  options = {}
} = {}) {
  if (poolSize < 4) {
    throw new Error(
      `solveTierWeights requires a pool of at least 4 cards; got ${poolSize}`
    );
  }
  if (draws < 1) {
    throw new Error(
      `solveTierWeights requires at least 1 draw; got ${draws}`
    );
  }
  const cacheable =
    targets === RISK_TIER_CUMULATIVE_TARGETS &&
    Object.keys(options).length === 0;
  const cacheKey = `${poolSize}:${draws}`;
  if (cacheable) {
    const hit = defaultTierWeightCache.get(cacheKey);
    if (hit) return { ...hit };
  }

  const {
    maxOuterIter = 40,
    maxBisectIter = 80,
    bisectTol = 1e-6,
    outerTol = 1e-7,
    loBound = 1e-6,
    hiBound = 64
  } = options;

  const tierOrder = [
    RISK_TIER.VERY_HIGH,
    RISK_TIER.HIGH,
    RISK_TIER.LOW,
    RISK_TIER.VERY_LOW
  ];
  const standardCount = poolSize - 4;
  const weights = [1, 1, 1, 1];

  for (let outer = 0; outer < maxOuterIter; outer++) {
    let maxChange = 0;
    for (let t = 0; t < tierOrder.length; t++) {
      const target = targets[tierOrder[t]];
      const before = weights[t];
      const next = bisectForTier({
        weights,
        tierIdx: t,
        standardCount,
        draws,
        target,
        lo: loBound,
        hi: hiBound,
        tol: bisectTol,
        maxIter: maxBisectIter
      });
      weights[t] = next;
      maxChange = Math.max(maxChange, Math.abs(next - before));
    }
    if (maxChange < outerTol) break;
  }

  const solved = {
    [RISK_TIER.VERY_HIGH]: weights[0],
    [RISK_TIER.HIGH]: weights[1],
    [RISK_TIER.LOW]: weights[2],
    [RISK_TIER.VERY_LOW]: weights[3]
  };
  if (cacheable) defaultTierWeightCache.set(cacheKey, solved);
  return { ...solved };
}

export function tierInclusionProbabilities({
  tierWeights,
  poolSize,
  draws
}) {
  const standardCount = poolSize - 4;
  const order = [
    RISK_TIER.VERY_HIGH,
    RISK_TIER.HIGH,
    RISK_TIER.LOW,
    RISK_TIER.VERY_LOW
  ];
  const arr = order.map((t) => tierWeights[t]);
  const out = {};
  for (let i = 0; i < order.length; i++) {
    out[order[i]] = inclusionForTier(arr, i, standardCount, draws);
  }
  return out;
}
